// src/web/client/src/pages/Billing.jsx - FINAL FIXED VERSION
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Check, 
  X, 
  Package, 
  CreditCard,
  AlertCircle,
  Loader2
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import { authenticatedFetch } from "@/utils/authenticatedFetch";

// API URL constant - IMPORTANT!
const API_URL = import.meta.env.VITE_API_URL || 'https://api.ecomoria.com';

// Feature component
const PlanFeature = ({ included = false, children }) => (
  <li className="flex items-start gap-2">
    {included ? (
      <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
    ) : (
      <X className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
    )}
    <span className={`text-sm ${!included && 'text-gray-500'}`}>{children}</span>
  </li>
);

export default function Billing() {
  const { toast } = useToast();
  const navigate = useNavigate();  const [shop, setShop] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpgrading, setIsUpgrading] = useState(false);

// Handle success param from billing callback (show only once)
useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const success = urlParams.get('success');
  const plan = urlParams.get('plan');
  const error = urlParams.get('error');
  
  // Use sessionStorage to show message only once
  const messageShown = sessionStorage.getItem('billing_message_shown');
   
  if (success === 'true') {
    if (!messageShown) {
      sessionStorage.setItem('billing_message_shown', 'true');
      toast({
        title: "Success!",
        description: `Your plan has been upgraded to ${plan || 'Pro'}`,
      });
    }
    
    // Always redirect to dashboard when success param exists
    // Redirect to dashboard using React Router
   /* setTimeout(() => {
      navigate('/dashboard');
    }, 1500);*/
  }
  if (error && !messageShown) {
    sessionStorage.setItem('billing_message_shown', 'true');
    toast({
      title: "Error",
      description: error.replace(/_/g, ' '),
      variant: "destructive"
    });
  }
  
  // Clear the flag when navigating away
  return () => {
    if (!success && !error) {
      sessionStorage.removeItem('billing_message_shown');
    }
  };
}, []);  
// ⚠️ KNOWN DUPLICATION — plan names, prices and limits are hardcoded here.
  //
  // The backend consolidated its four copies into src/backend/config/plans.js
  // (one definition, a projection per consumer). The FRONTEND could not join
  // that consolidation because no plan-catalogue endpoint exists to source
  // these from — building one is a separate change and out of scope here.
  //
  // So this object, the plan-card JSX below, Landing.jsx's `pricingPlans` and
  // SubscriptionAlert.jsx's upgrade CTA remain hand-maintained copies. Adding
  // the Scale tier widened that duplication from two paid plans to three; it
  // did not create it. Anything changed here must be changed in
  // config/plans.js in the same commit, and vice versa.
  const plans = {
    // Rendered in the Current Plan card when the merchant has not yet
    // approved a Shopify AppSubscription (status pending/cancelled/expired).
    // The backend /status endpoint returns plan:'none' for this state so
    // the UI shows "Choose a plan" instead of defaulting to Basic.
    none: {
      name: "No Plan Selected",
      price: 0,
      features: [],
      limitations: []
    },
    trial: {
      name: "Free Trial",
      price: 0,
      features: [
        "Up to 100 messages per month",
        "Thank-you emails",
        "Review requests",
        "Basic analytics",
        "90-day free trial"
      ],
      limitations: [
        "Limited to 100 messages",
"Basic templates only",
        "Basic templates only"
      ]
    },
    basic: {
      name: "Basic Plan",
      // ⚠️ DUPLICATED FROM THE BACKEND. Prices and limits here must match
      // src/backend/config/plans.js (the single source of truth) and the
      // published App Store listing. There is still no plan-catalogue
      // endpoint to source them from — see the note above `plans`.
      price: 29.99,
      orders: 300,
      features: [
        "Up to 300 orders per month",
        "Up to 1,000 messages per month",
        "Thank-you emails",
        "Review requests",
        "Basic analytics",
        "Email support"
      ],
      limitations: [
"Standard support",
        "Basic templates only",
        "Standard support"
      ]
    },
    pro: {
      name: "Pro Plan",
      // ⚠️ DUPLICATED — see the note on `basic` above.
      price: 49.99,
      orders: 1500,
      features: [
        "Up to 1,500 orders per month",
        "Unlimited messages",
        "All message types (Thank you, Review, VIP, Custom)",
        "AI-powered personalization",
        "VIP customer identification",
"Advanced personalization",
        "Smart send timing optimization",
        "A/B testing for messages",
        "Revenue attribution tracking",
        "Customer segmentation",
        "Advanced analytics dashboard",
        "Priority support"
      ]
    },
    // The high-volume tier. Feature-identical to Pro — what it sells is order
    // headroom (1,500 → 5,000/month), so the feature list leads with the
    // volume line and then says "everything in Pro" rather than restating
    // twelve identical bullets.
    scale: {
      name: "Scale Plan",
      // ⚠️ DUPLICATED — see the note on `basic` above.
      price: 99.99,
      orders: 5000,
      features: [
        "Up to 5,000 orders per month",
        "Unlimited messages",
        "Everything in Pro, included",
        "AI-powered personalization",
        "VIP customer identification",
        "Smart send timing optimization",
        "A/B testing for messages",
        "Revenue attribution tracking",
        "Advanced analytics dashboard",
        "Priority support"
      ]
    },
    // The Ecomoria Platform ("brain") tier: everything in Scale, twice the
    // order headroom, and the Platform screens + brain-driven sends.
    platform: {
      name: "Platform Plan",
      // ⚠️ DUPLICATED — see the note on `basic` above.
      price: 199.99,
      orders: 10000,
      features: [
        "Up to 10,000 orders per month",
        "Unlimited messages",
        "Everything in Scale, included",
        "The Ecomoria Platform: customer value, churn risk, lifecycle stage",
        "Priority support"
      ]
    }
  };

  useEffect(() => {
    loadBillingStatus();
  }, []);

  const loadBillingStatus = async () => {
    setIsLoading(true);
    try {
      const shopDomain = localStorage.getItem('shopDomain');
      console.log('Loading billing for shop:', shopDomain);
      
      if (!shopDomain) {
        toast({
          title: "Error",
          description: "Please connect your shop first",
          variant: "destructive"
        });
        return;
      }

      // Get billing status from Shopify billing endpoint
     // const response = await fetch(`${API_URL}/api/shopify-billing/status?shop_domain=${shopDomain}`);
      const response = await authenticatedFetch(`${API_URL}/api/shopify-billing/status?shop_domain=${shopDomain}`);
      if (!response.ok) {
        throw new Error('Failed to fetch billing status');
      }

      const data = await response.json();
      console.log('Billing status:', data);
      setBillingStatus(data);
      
      // Set shop data for backward compatibility
      setShop({
        shop_domain: shopDomain,
        plan: data.plan,
        billing: {
          messages_sent_this_month: data.messages_used,
          messages_limit: data.messages_limit,
          subscription_status: data.status,
          trial_ends_at: data.trial_days_left > 0 
            ? new Date(Date.now() + data.trial_days_left * 24 * 60 * 60 * 1000).toISOString()
            : null
        }
      });
      
    } catch (error) {
      console.error('Error loading billing status:', error);
      toast({
        title: "Error",
        description: "Failed to load billing information",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  /*const handleUpgrade = async () => {
    setIsUpgrading(true);
    try {
      const shopDomain = localStorage.getItem('shopDomain');
      console.log('Upgrading for shop:', shopDomain);
      
      if (!shopDomain) {
        throw new Error('No shop domain found. Please connect your shop first.');
      }
      
      const response = await authenticatedFetch(`${API_URL}/api/shopify-billing/create-charge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          shop_domain: shopDomain, 
          plan: 'pro' 
        })
      });

      const data = await response.json();
      console.log('Billing response:', data);

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to create charge');
      }

      const { confirmation_url } = data;
      
      // Redirect to Shopify confirmation page
      //window.location.href = confirmation_url;
      // Redirect to Shopify confirmation page (must open in top window, not iframe)
      if (window.top !== window.self) {
        // We're in an iframe - open in top window
        window.top.location.href = confirmation_url;
      } else {
        window.location.href = confirmation_url;
      }
      
    } catch (error) {
      console.error('Upgrade error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to start upgrade process",
        variant: "destructive"
      });
      setIsUpgrading(false);
    }
  };*/

  const handleSelectPlan = async (selectedPlan) => {
    setIsUpgrading(true);
    try {
      const shopDomain = localStorage.getItem('shopDomain');
      console.log('Selecting plan:', selectedPlan, 'for shop:', shopDomain);
      
      if (!shopDomain) {
        throw new Error('No shop domain found. Please connect your shop first.');
      }
      
      const response = await authenticatedFetch(`${API_URL}/api/shopify-billing/create-charge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          shop_domain: shopDomain, 
          plan: selectedPlan 
        })
      });

      const data = await response.json();
      console.log('Billing response:', data);

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to create charge');
      }

      const { confirmation_url } = data;
      
      // Redirect to Shopify confirmation page (must open in top window, not iframe)
      if (window.top !== window.self) {
        window.top.location.href = confirmation_url;
      } else {
        window.location.href = confirmation_url;
      }
      
    } catch (error) {
      console.error('Plan selection error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to start upgrade process",
        variant: "destructive"
      });
      setIsUpgrading(false);
    }
  };

  // Keep handleUpgrade for backward compatibility
  const handleUpgrade = () => handleSelectPlan('pro');
  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    try {
      const shopDomain = localStorage.getItem('shopDomain');
      
      const response = await authenticatedFetch(`${API_URL}/api/shopify-billing/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_domain: shopDomain })
      });

      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }

      toast({
        title: "Subscription Cancelled",
        description: "Your subscription has been cancelled",
      });
      
      // Reload billing status
      loadBillingStatus();
      
    } catch (error) {
      console.error('Cancel error:', error);
      toast({
        title: "Error",
        description: "Failed to cancel subscription",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  // The paid tiers, cheapest first — the same order as BILLABLE_PLAN_KEYS in
  // src/backend/config/plans.js minus `trial`. Written out here because the
  // frontend has no plan-catalogue endpoint to read it from (see the note on
  // `plans` above). Used for every "is this merchant on a paid plan?" check,
  // so adding a tier is one entry rather than a new `|| plan === "..."` in
  // half a dozen conditions.
  const PAID_PLANS = ["basic", "pro", "scale", "platform"];
  const isPaidPlan = (p) => PAID_PLANS.includes(p);
  // Billing-exempt shop (backend config/billingExempt.js, BILLING_EXEMPT_SHOPS):
  // /shopify-billing/status reports billing_exempt:true. The ONE frontend
  // branch for it — the price line, the plan cards and the Cancel button give
  // way to a single complimentary-plan sentence. Absent/false → page unchanged.
  const isBillingExempt = billingStatus?.billing_exempt === true;
  // Plans with no message cap. Keyed off the plan only as a FALLBACK — the
  // -1 sentinel on the stored limit is still the real answer.
  const UNLIMITED_MESSAGE_PLANS = ["pro", "scale"];

  // Usage display state. Unlimited is keyed on the backend's -1 sentinel
  // (BILLING_PLANS pro.features.messages_per_month), not the plan name;
  // the fallback mirrors the previous defaults for a missing limit.
  const messagesUsed = shop?.billing?.messages_sent_this_month || 0;
  const messagesLimit = shop?.billing?.messages_limit
    || (UNLIMITED_MESSAGE_PLANS.includes(shop?.plan) ? -1 : 1000);
  const isUnlimited = messagesLimit === -1;
  const usagePercent = isUnlimited ? 0 : (messagesUsed / messagesLimit) * 100;

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing & Plans</h1>
          <p className="text-gray-500">
            {isBillingExempt
              ? "Your store is set up with no billing attached."
              : "Choose the right plan for your business"}
          </p>
        </div>

        {/* Trial Alert */}
        {billingStatus?.trial_active && billingStatus?.trial_days_left <= 7 && (
          <Alert className="border-orange-200 bg-orange-50">
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              Your free trial ends in {billingStatus.trial_days_left} days. 
              Upgrade now to continue sending messages without interruption.
            </AlertDescription>
          </Alert>
        )}

        {/* Current Plan Card */}
        <Card>
          <CardHeader>
            <CardTitle>Current Plan</CardTitle>
            <CardDescription>
              Your current subscription and usage
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                <Package className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold capitalize">
                  {(plans[shop?.plan] || plans.none).name}
                </h3>
                <p className="text-sm text-gray-500">
                  {isBillingExempt
                    ? "Your store is on a complimentary plan. No charges apply to this shop."
                    : isPaidPlan(shop?.plan)
                    ? `$${plans[shop.plan].price}/month · up to ${plans[shop.plan].orders.toLocaleString()} orders`
                    : "Choose a plan below to get started"}
                </p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {isPaidPlan(shop?.plan) && (
                  <Badge className={
                    shop?.plan === "platform" ? "bg-indigo-100 text-indigo-800"
                      : shop?.plan === "scale" ? "bg-purple-100 text-purple-800"
                        : shop?.plan === "pro" ? "bg-green-100 text-green-800"
                          : "bg-blue-100 text-blue-800"}>
                    {shop?.plan === "platform" ? "Platform" : shop?.plan === "scale" ? "Scale" : shop?.plan === "pro" ? "Pro" : "Basic"}
                  </Badge>
                )}
                {billingStatus?.trial_active && (
                  <Badge variant="outline">
                    Trial - {billingStatus.trial_days_left} days left
                  </Badge>
                )}
              </div>
            </div>

            {shop && isPaidPlan(shop.plan) && (
              <div className="mt-6 space-y-4">
                <div>
                  {isUnlimited ? (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-900">{messagesUsed.toLocaleString()}</span>
                      {" "}messages this month · Unlimited
                    </p>
                  ) : (
                    <>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Messages This Month</span>
                        <span className={`font-medium ${
                          usagePercent >= 100 ? "text-red-600" : usagePercent >= 80 ? "text-amber-600" : ""
                        }`}>
                          {messagesUsed.toLocaleString()} / {messagesLimit.toLocaleString()}
                        </span>
                      </div>
                      <Progress
                        value={Math.min(usagePercent, 100)}
                        className={`h-2 ${
                          usagePercent >= 100 ? "[&>div]:bg-red-500" : usagePercent >= 80 ? "[&>div]:bg-amber-500" : ""
                        }`}
                      />
                    </>
                  )}
                </div>

                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    {billingStatus?.trial_active ? (
                      <>Trial ends: {format(new Date(shop.billing?.trial_ends_at), "MMM d, yyyy")}</>
                    ) : (
                      <>Status: {billingStatus?.status || 'Inactive'}</>
                    )}
                  </div>
                  {!isBillingExempt && (shop.plan === 'pro' || shop.plan === 'scale' || shop.plan === 'platform') && billingStatus?.status === 'active' && (
                    <Button 
                      size="sm"
                      variant="outline" 
                      onClick={handleCancel}
                    >
                      Cancel Subscription
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plans Grid — four paid options since the Platform tier landed.
            Hidden entirely for a billing-exempt shop (see isBillingExempt). */}
        {!isBillingExempt && (
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          <Card className="border-2 border-gray-200">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Basic Plan</span>
                <Badge variant="outline" className="text-gray-600">
                  ${plans.basic.price}/month
                </Badge>
              </CardTitle>
              <CardDescription>For small stores just getting started</CardDescription>
              <p className="text-xs text-gray-500 mt-1">90-day free trial · Cancel anytime</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-2">
                {plans.basic.features.map((feature, index) => (
                  <PlanFeature key={index} included>{feature}</PlanFeature>
                ))}
                {plans.basic.limitations.map((limitation, index) => (
                  <PlanFeature key={`limit-${index}`}>{limitation}</PlanFeature>
                ))}
              </ul>
              {shop?.plan === "basic" ? (
                <Button disabled variant="outline" className="w-full">
                  Current Plan
                </Button>
             ) : (shop?.plan === "pro" || shop?.plan === "scale" || shop?.plan === "platform") ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleSelectPlan('basic')}
                disabled={isUpgrading}
              >
                {isUpgrading ? 'Processing...' : 'Downgrade to Basic'}
              </Button>
              ) : (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => handleSelectPlan('basic')}
                  disabled={isUpgrading}
                >
                  {isUpgrading ? 'Processing...' : 'Choose Basic'}
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="border-2 border-indigo-200 relative">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-indigo-600 text-white text-xs px-3 py-1 rounded-full">
              RECOMMENDED
            </div>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Pro Plan</span>
                <Badge variant="outline" className="text-indigo-600 border-indigo-200">
                  ${plans.pro.price}/month
                </Badge>
              </CardTitle>
              <CardDescription>For growing stores up to 1,500 orders a month</CardDescription>
              <p className="text-xs text-gray-500 mt-1">90-day free trial · Cancel anytime</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-2">
                {plans.pro.features.map((feature, index) => (
                  <PlanFeature key={index} included>{feature}</PlanFeature>
                ))}
              </ul>
              {shop?.plan === "pro" ? (
                <Button disabled className="w-full">
                  Current Plan
                </Button>
              ) : (shop?.plan === "scale" || shop?.plan === "platform") ? (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleSelectPlan('pro')}
                  disabled={isUpgrading}
                >
                  {isUpgrading ? 'Processing...' : 'Downgrade to Pro'}
                </Button>
              ) : (
                <Button 
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleUpgrade}
                  disabled={isUpgrading}
                >
                  {isUpgrading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Upgrade Now"
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Scale — the high-volume tier. Same card shape as the other two;
              the only thing it sells over Pro is order headroom, so the
              feature list leads with the volume line. */}
          <Card className="border-2 border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Scale Plan</span>
                <Badge variant="outline" className="text-purple-600 border-purple-200">
                  ${plans.scale.price}/month
                </Badge>
              </CardTitle>
              <CardDescription>For high-volume stores past 1,500 orders a month</CardDescription>
              <p className="text-xs text-gray-500 mt-1">90-day free trial · Cancel anytime</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-2">
                {plans.scale.features.map((feature, index) => (
                  <PlanFeature key={index} included>{feature}</PlanFeature>
                ))}
              </ul>
              {shop?.plan === "scale" ? (
                <Button disabled className="w-full">
                  Current Plan
                </Button>
              ) : shop?.plan === "platform" ? (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleSelectPlan('scale')}
                  disabled={isUpgrading}
                >
                  {isUpgrading ? 'Processing...' : 'Downgrade to Scale'}
                </Button>
              ) : (
                <Button
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  onClick={() => handleSelectPlan('scale')}
                  disabled={isUpgrading}
                >
                  {isUpgrading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Choose Scale"
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Platform — the Ecomoria brain tier. Same card shape; what it sells
              over Scale is the Platform itself plus order headroom. */}
          <Card className="border-2 border-indigo-300">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Platform Plan</span>
                <Badge variant="outline" className="text-indigo-600 border-indigo-200">
                  ${plans.platform.price}/month
                </Badge>
              </CardTitle>
              <CardDescription>Customer value, churn risk and lifecycle stage for every customer</CardDescription>
              <p className="text-xs text-gray-500 mt-1">Two-week free trial · Cancel anytime</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-2">
                {plans.platform.features.map((feature, index) => (
                  <PlanFeature key={index} included>{feature}</PlanFeature>
                ))}
              </ul>
              {shop?.plan === "platform" ? (
                <Button disabled className="w-full">
                  Current Plan
                </Button>
              ) : (
                <Button
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => handleSelectPlan('platform')}
                  disabled={isUpgrading}
                >
                  {isUpgrading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Choose Platform"
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
        )}

        {/* FAQ */}
        {!isBillingExempt && (
        <Card>
          <CardHeader>
            <CardTitle>Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold mb-1">What's included in each plan?</h3>
              <p className="text-sm text-gray-600">
                Every plan includes a monthly order allowance — {plans.basic.orders.toLocaleString()} on Basic,
                {" "}{plans.pro.orders.toLocaleString()} on Pro, {plans.scale.orders.toLocaleString()} on Scale
                and {plans.platform.orders.toLocaleString()} on Platform.
                Pro adds unlimited messages, advanced analytics and priority support;
                Scale is Pro with room for a lot more orders.
                Platform adds customer value, churn risk and lifecycle stage for every customer.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">What happens if I go over my order limit?</h3>
              <p className="text-sm text-gray-600">
                Nothing stops. We keep sending every email and let you know you've gone
                past your plan, so you can upgrade when it suits you — your customers
                never miss a thank-you or a review request in the meantime.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">Can I change plans anytime?</h3>
              <p className="text-sm text-gray-600">
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-1">What happens after my trial ends?</h3>
              <p className="text-sm text-gray-600">
                After your 90-day trial, you'll need to choose a plan to continue sending messages.
              </p>
            </div>
          </CardContent>
        </Card>
        )}
      </div>
    </div>
  );
}